/** Достижения (GET /users/@me/achievements, событие ACHIEVEMENT_UNLOCK) и значки профиля. */
import { BadgeCheck, Code, Crown, Flame, Gem, Layers, MessageSquare, Mic, Moon, MonitorUp, Music, Radio, Star, Trophy, Truck, Users, type LucideIcon } from 'lucide-react'
import { authed } from './auth'

/** Значок и цвет плитки достижения: у каждого свой, как на макете. */
export const ACHIEVEMENT_LOOK: Record<string, { icon: LucideIcon; from: string; to: string }> = {
  first_message: { icon: MessageSquare, from: '#8fd3ff', to: '#3b82f6' },
  voice_100h: { icon: Mic, from: '#ff9a7a', to: '#e8505b' },
  invites_5: { icon: Radio, from: '#c7cdff', to: '#7d88ff' },
  booster: { icon: Gem, from: '#ff9ef0', to: '#d65cff' },
  streams_10: { icon: MonitorUp, from: '#ffb86b', to: '#f06d3a' },
  streak_7: { icon: Flame, from: '#ffe08a', to: '#f0a020' },
  founder_25: { icon: Crown, from: '#fff1a8', to: '#e2b53c' },
  dj_50: { icon: Music, from: '#7ee29f', to: '#2f9e5a' },
  night_20h: { icon: Moon, from: '#8f98ff', to: '#2b2f7a' },
  collector_5: { icon: Layers, from: '#d6c2ff', to: '#8b6cf0' },
  import: { icon: Truck, from: '#9ae6ff', to: '#3aa6c9' },
}

export const achievementLook = (id: string) => ACHIEVEMENT_LOOK[id] ?? { icon: Trophy, from: '#c7cdff', to: '#5865f2' }

/** Как включить музыку — подсказка к «Диджею» в достижениях и в магазине. */
export const MUSIC_HOW = 'Зайдите в голосовой канал и напишите в чат сервера /играй и название песни. Бот зайдёт и включит её.'

/** Значки профиля: иконка, фон и цвет иконки (backend/internal/achieve/profile.go). */
/** Пояснение значка в подсказке: за что он. Условия — backend/internal/achieve/profile.go. */
export const BADGE_INFO: Record<string, string> = {
  staff: 'Делает Кличат',
  early: 'Учётная запись создана до 2027 года',
  plus: 'Подписка Кличат Плюс',
  booster: 'Бустит сервер',
  friends: '5 друзей пришли по приглашению',
  voice: '100 часов в голосовых каналах',
  dev: 'Делает ботов для Кличата',
}

/** Значки с картинкой из ChatGPT (docs/badges, выгрузка export.py); без картинки — BADGE_LOOK ниже. */
export const BADGE_ART = new Set(['staff', 'early', 'booster', 'friends', 'voice', 'dev'])

export const BADGE_LOOK: Record<string, { icon: LucideIcon; bg: string; fg: string }> = {
  staff: { icon: BadgeCheck, bg: 'linear-gradient(135deg, #8f98ff, #5865f2)', fg: '#ffffff' },
  early: { icon: Star, bg: 'linear-gradient(135deg, #ffe08a, #f0a020)', fg: '#3a2500' },
  booster: { icon: Gem, bg: 'linear-gradient(135deg, #ff9ef0, #d65cff)', fg: '#3b0a3a' },
  friends: { icon: Users, bg: 'linear-gradient(135deg, #c7cdff, #7d88ff)', fg: '#16194a' },
  voice: { icon: Mic, bg: 'linear-gradient(135deg, #ff9a7a, #e8505b)', fg: '#3b0a0a' },
  dev: { icon: Code, bg: 'linear-gradient(135deg, #7ee29f, #2f9e5a)', fg: '#06240f' },
}

/** Награда: предмет украшений и его название («Рамка «Клич»»). */
export interface Reward {
  item: string
  name: string
}

export interface Achievement {
  id: string
  name: string
  description: string
  /** Цель и прогресс — в показанных единицах: часы, дни, треки. */
  goal: number
  progress: number
  /** Единица в родительном падеже: «из 20 часов». */
  unit: string
  rewards: Reward[]
  earned_at: string | null
  /** Получено, а раздел ещё не открывали. */
  new: boolean
}

/** Событие ACHIEVEMENT_UNLOCK. */
export interface Unlock {
  id: string
  name: string
  rewards: Reward[]
}

export const achieveApi = {
  list: () => authed<{ achievements: Achievement[] }>('/users/@me/achievements').then((r) => r.achievements),
  seen: () => authed<void>('/users/@me/achievements/seen', { method: 'POST' }),
}
